import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  AlertCircle,
  Users,
  UserCheck,
  Clock,
  Mail,
  Shield,
  Star,
  FileText,
  DollarSign,
  CreditCard,
  AlertTriangle,
  Download,
  Phone,
  Globe,
  MapPin,
  Tag,
  Ticket,
  FileSignature,
  Monitor,
  FolderKanban,
  Target,
  Wallet,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Settings,
  X,
  RotateCcw,
} from 'lucide-react';
import api, { adminInvoicesAPI, adminCompanyAPI, invoicesAPI, adminFeatureFlagsAPI, adminClientPaymentsAPI, displaySettingsAPI, adminBrandingAPI, adminComanagedAPI } from '../../services/api';
import DisplayOverridesEditor from '../../components/admin/DisplayOverridesEditor';
import CompanyBrandingSection from '../../components/admin/CompanyBrandingSection';
import CompanyComanagedSection from '../../components/admin/CompanyComanagedSection';
import { REGISTRY_CATEGORIES } from '../../config/displayRegistry';
import { StickyNote, Plus, Pin, Trash2, Edit2, Check, History, ChevronDown, ChevronUp, Filter, Palette, Lock, Wrench } from 'lucide-react';
import { useNotInPlanMessage } from '../../components/common/NotInPlan';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import DraggableSection from '../../components/admin/DraggableSection';
import { usePreferencesStore } from '../../context/preferencesStore';
import { formatMoney } from '../../utils/format';
import { notifyError } from '../../context/toastStore';

const roleBadges = {
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  technician: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  finance: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  company_admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  user: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
};

function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

// Payment Settings Section Component
function PaymentSettingsSection({ paymentSettings, loading, onUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    depositsEnabled: null,
    creditCardSurchargeEnabled: null,
    creditCardSurchargeType: 'percentage',
    creditCardSurchargeValue: '',
  });

  const handleStartEdit = () => {
    if (paymentSettings?.companySettings) {
      setFormData({
        depositsEnabled: paymentSettings.companySettings.depositsEnabled,
        creditCardSurchargeEnabled: paymentSettings.companySettings.creditCardSurchargeEnabled,
        creditCardSurchargeType: paymentSettings.companySettings.creditCardSurchargeType || 'percentage',
        creditCardSurchargeValue: paymentSettings.companySettings.creditCardSurchargeValue?.toString() || '',
      });
    } else {
      setFormData({
        depositsEnabled: paymentSettings?.globalDefaults?.depositsEnabled ?? true,
        creditCardSurchargeEnabled: paymentSettings?.globalDefaults?.creditCardSurchargeEnabled ?? true,
        creditCardSurchargeType: paymentSettings?.globalDefaults?.creditCardSurchargeType || 'percentage',
        creditCardSurchargeValue: paymentSettings?.globalDefaults?.creditCardSurchargeValue?.toString() || '4',
      });
    }
    setEditMode(true);
  };

  const handleCancel = () => setEditMode(false);

  const handleSave = async () => {
    await onUpdate({
      depositsEnabled: formData.depositsEnabled,
      creditCardSurchargeEnabled: formData.creditCardSurchargeEnabled,
      creditCardSurchargeType: formData.creditCardSurchargeType,
      creditCardSurchargeValue: formData.creditCardSurchargeValue ? parseFloat(formData.creditCardSurchargeValue) : null,
    });
    setEditMode(false);
  };

  const handleUseGlobalDefaults = async () => {
    await onUpdate({ useGlobalDefaults: true });
    setEditMode(false);
  };

  const globalDefaults = paymentSettings?.globalDefaults || {
    depositsEnabled: true,
    creditCardSurchargeEnabled: true,
    creditCardSurchargeType: 'percentage',
    creditCardSurchargeValue: 4,
  };

  const effectiveSettings = paymentSettings?.effectiveSettings || globalDefaults;
  const hasOverride = !!paymentSettings?.companySettings;

  if (editMode) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium">Customize Payment Settings</p>
          <p className="mt-1">Set custom payment settings for this company. Click "Use Global Defaults" to inherit portal settings.</p>
        </div>

        {/* Deposit Payments Toggle */}
        <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <div>
            <span className="font-medium text-neutral-900 dark:text-white">Quote Deposit Payments</span>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Allow customers to pay deposits on accepted quotes</p>
          </div>
          <button
            onClick={() => setFormData(prev => ({ ...prev, depositsEnabled: !prev.depositsEnabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
              formData.depositsEnabled ? 'bg-green-500' : 'bg-neutral-300'
            }`}
          >
            <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              formData.depositsEnabled ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>

        <div className="border-t border-neutral-200 dark:border-neutral-700" />

        {/* Surcharge Enabled Toggle */}
        <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <div>
            <span className="font-medium text-neutral-900 dark:text-white">Enable Credit Card Surcharge</span>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Apply a surcharge to credit card payments</p>
          </div>
          <button
            onClick={() => setFormData(prev => ({ ...prev, creditCardSurchargeEnabled: !prev.creditCardSurchargeEnabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
              formData.creditCardSurchargeEnabled ? 'bg-green-500' : 'bg-neutral-300'
            }`}
          >
            <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              formData.creditCardSurchargeEnabled ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {/* Surcharge Amount */}
        {formData.creditCardSurchargeEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Surcharge Type</label>
              <select
                value={formData.creditCardSurchargeType}
                onChange={(e) => setFormData(prev => ({ ...prev, creditCardSurchargeType: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                {formData.creditCardSurchargeType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.creditCardSurchargeValue}
                onChange={(e) => setFormData(prev => ({ ...prev, creditCardSurchargeValue: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                placeholder={formData.creditCardSurchargeType === 'percentage' ? '4' : '5.00'}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <button onClick={handleUseGlobalDefaults} disabled={loading} className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200">
            Use Global Defaults
          </button>
          <div className="flex gap-2">
            <button onClick={handleCancel} disabled={loading} className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
              Cancel
            </button>
            <button onClick={handleSave} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      <div className={`flex items-center justify-between p-3 rounded-lg ${
        hasOverride
          ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
          : 'bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-200 dark:border-neutral-600'
      }`}>
        <div className="flex items-center gap-2">
          {hasOverride ? (
            <>
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded">Custom Settings</span>
              <span className="text-sm text-neutral-600 dark:text-neutral-300">This company uses custom payment settings</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-300">Using global defaults from Portal Settings</span>
            </>
          )}
        </div>
        <button onClick={handleStartEdit} className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
          {hasOverride ? 'Edit' : 'Customize'}
        </button>
      </div>

      {/* Current Settings Display */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Deposit Payments</p>
          <p className="font-medium text-neutral-900 dark:text-white">
            {effectiveSettings.depositsEnabled ? (
              <span className="text-green-600 dark:text-green-400">Enabled</span>
            ) : (
              <span className="text-red-500">Disabled</span>
            )}
          </p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">CC Surcharge</p>
          <p className="font-medium text-neutral-900 dark:text-white">
            {effectiveSettings.creditCardSurchargeEnabled ? (
              effectiveSettings.creditCardSurchargeType === 'fixed'
                ? formatMoney(effectiveSettings.creditCardSurchargeValue || 0)
                : `${effectiveSettings.creditCardSurchargeValue}%`
            ) : (
              <span className="text-neutral-500">Disabled</span>
            )}
          </p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Surcharge Type</p>
          <p className="font-medium text-neutral-900 dark:text-white capitalize">
            {effectiveSettings.creditCardSurchargeEnabled
              ? (effectiveSettings.creditCardSurchargeType || 'percentage')
              : <span className="text-neutral-500">N/A</span>
            }
          </p>
        </div>
      </div>

      {/* Global Defaults Reference */}
      <div className="text-xs text-neutral-400 dark:text-neutral-500">
        <span className="font-medium">Global defaults:</span>{' '}
        Deposits {globalDefaults.depositsEnabled ? 'enabled' : 'disabled'} ·{' '}
        {globalDefaults.creditCardSurchargeEnabled
          ? `${globalDefaults.creditCardSurchargeType === 'fixed' ? '$' : ''}${globalDefaults.creditCardSurchargeValue}${globalDefaults.creditCardSurchargeType === 'percentage' ? '%' : ''} surcharge`
          : 'No surcharge'
        }
      </div>
    </div>
  );
}

/**
 * Per-company display customization editor. Uses the shared
 * DisplayOverridesEditor and adds a "Using global defaults" banner + whole-
 * page reset button. The editor's inheritedOverrides is the global overrides
 * map from the backend so cleared rows show the global label as placeholder.
 */
function CompanyDisplaySettingsSection({ displaySettings, loading, onSave, onUseGlobalDefaults }) {
  const [draft, setDraft] = useState(displaySettings?.companyOverrides || {});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(displaySettings?.companyOverrides || {});
    setDirty(false);
  }, [displaySettings]);

  if (loading && !displaySettings) {
    return <div className="p-6 text-center text-neutral-500">Loading display settings…</div>;
  }

  const usingGlobalDefaults = displaySettings?.usingGlobalDefaults;
  const overrideCount = Object.keys(draft).length;

  return (
    <div className="p-6 space-y-4">
      {usingGlobalDefaults && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
          This company is using the global display defaults. Any override you add below will
          be applied only for this company.
        </div>
      )}
      {!usingGlobalDefaults && (
        <div className="flex items-start justify-between gap-3 p-3 bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 rounded-lg">
          <div className="text-sm text-neutral-700 dark:text-neutral-300">
            <strong>{overrideCount}</strong> custom {overrideCount === 1 ? 'override' : 'overrides'} for this company.
            Uncustomized fields inherit the global defaults.
          </div>
          <button
            type="button"
            onClick={onUseGlobalDefaults}
            disabled={loading}
            className="text-xs px-3 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 whitespace-nowrap disabled:opacity-50"
          >
            Use Global Defaults for Entire Page
          </button>
        </div>
      )}

      <div className="space-y-6">
        {REGISTRY_CATEGORIES.map(category => (
          <div key={category}>
            <h4 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">{category}</h4>
            <DisplayOverridesEditor
              category={category}
              overrides={draft}
              onChange={(next) => { setDraft(next); setDirty(true); }}
              inheritedOverrides={displaySettings?.globalOverrides || {}}
              showInheritColumn
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={!dirty || loading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {loading ? 'Saving…' : 'Save Company Overrides'}
        </button>
      </div>
    </div>
  );
}

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [ticketData, setTicketData] = useState({ tickets: [], stats: null });
  const [agreements, setAgreements] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [recurringPayments, setRecurringPayments] = useState([]);
  const [autopay, setAutopay] = useState(null);
  const [featureFlags, setFeatureFlags] = useState([]);
  const notInPlan = useNotInPlanMessage();
  const [flagsLoading, setFlagsLoading] = useState({});
  const [notes, setNotes] = useState([]);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);
  const [displaySettings, setDisplaySettings] = useState(null);
  const [displaySettingsLoading, setDisplaySettingsLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [comanaged, setComanaged] = useState(null);
  const [comanagedLoading, setComanagedLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recurring payments filter and expanded state
  const [showCancelledRecurring, setShowCancelledRecurring] = useState(false);
  const [expandedRecurringId, setExpandedRecurringId] = useState(null);

  // Layout customization state
  const [editMode, setEditMode] = useState(false);
  const [tempSections, setTempSections] = useState(null);
  const { companySections, setSectionOrder, getDefaults } = usePreferencesStore();

  // Which fetch is current: switching company mid-flight used to let the
  // previous company's slower response land on top of the new one.
  const fetchRun = useRef(0);
  useEffect(() => {
    fetchCompanyData();
    return () => { fetchRun.current += 1; };
  }, [companyId]);

  const fetchCompanyData = async () => {
    const run = ++fetchRun.current;
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel with graceful degradation
      const [
        companyRes,
        statsRes,
        invoicesRes,
        paymentsRes,
        contactsRes,
        ticketsRes,
        agreementsRes,
        configurationsRes,
        projectsRes,
        opportunitiesRes,
        paymentMethodsRes,
        recurringPaymentsRes,
        autopayRes,
        featureFlagsRes,
        notesRes,
        tagsRes,
        allTagsRes,
        paymentSettingsRes,
        displaySettingsRes,
        brandingRes,
        comanagedRes,
      ] = await Promise.all([
        api.get(`/admin/companies/${companyId}`),
        adminInvoicesAPI.getCompanyInvoiceStats(companyId).catch(() => ({ data: { stats: null } })),
        adminInvoicesAPI.getCompanyInvoices(companyId, { limit: 5, status: 'open' }).catch(() => ({ data: { invoices: [] } })),
        adminInvoicesAPI.getCompanyPayments(companyId, { limit: 5 }).catch(() => ({ data: { payments: [] } })),
        adminCompanyAPI.getContacts(companyId).catch(() => ({ data: { contacts: [] } })),
        adminCompanyAPI.getTickets(companyId, { pageSize: 5, includeStats: true }).catch(() => ({ data: { tickets: [], stats: null } })),
        adminCompanyAPI.getAgreements(companyId).catch(() => ({ data: { agreements: [] } })),
        adminCompanyAPI.getConfigurations(companyId, { pageSize: 10 }).catch(() => ({ data: { configurations: [] } })),
        adminCompanyAPI.getProjects(companyId).catch(() => ({ data: { projects: [] } })),
        adminCompanyAPI.getOpportunities(companyId).catch(() => ({ data: { opportunities: [] } })),
        adminCompanyAPI.getPaymentMethods(companyId).catch(() => ({ data: { paymentMethods: [] } })),
        adminClientPaymentsAPI.getRecurringPayments(companyId).catch(() => ({ data: { recurringPayments: [] } })),
        adminCompanyAPI.getAutopay(companyId).catch(() => ({ data: { autopay: null } })),
        adminFeatureFlagsAPI.getCompanyFlags(companyId).catch(() => ({ data: { flags: [] } })),
        adminCompanyAPI.getNotes(companyId).catch(() => ({ data: { notes: [] } })),
        adminCompanyAPI.getTags(companyId).catch(() => ({ data: { tags: [] } })),
        adminCompanyAPI.getAllTags().catch(() => ({ data: { tags: [] } })),
        adminCompanyAPI.getPaymentSettings(companyId).catch(() => ({ data: null })),
        displaySettingsAPI.adminGetCompany(companyId).catch(() => ({ data: null })),
        adminBrandingAPI.getCompany(companyId).catch(() => ({ data: null })),
        adminComanagedAPI.getCompany(companyId).catch(() => ({ data: null })),
      ]);

      if (run !== fetchRun.current) return;
      setCompany(companyRes.data.company);
      setUsers(companyRes.data.users);
      setInvoiceStats(statsRes.data.stats);
      setInvoices(invoicesRes.data.invoices);
      setPayments(paymentsRes.data.payments);
      setContacts(contactsRes.data.contacts || []);
      setTicketData({
        tickets: ticketsRes.data.tickets || [],
        stats: ticketsRes.data.stats,
      });
      setAgreements(agreementsRes.data.agreements || []);
      setConfigurations(configurationsRes.data.configurations || []);
      setProjects(projectsRes.data.projects || []);
      setOpportunities(opportunitiesRes.data.opportunities || []);
      setPaymentMethods(paymentMethodsRes.data.paymentMethods || []);
      setRecurringPayments(recurringPaymentsRes.data.recurringPayments || []);
      setAutopay(autopayRes.data.autopay);
      setFeatureFlags(featureFlagsRes.data.flags || []);
      setNotes(notesRes.data.notes || []);
      setTags(tagsRes.data.tags || []);
      setAllTags(allTagsRes.data.tags || []);
      setPaymentSettings(paymentSettingsRes.data);
      setDisplaySettings(displaySettingsRes.data);
      setBranding(brandingRes.data);
      setComanaged(comanagedRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDownloadPdf = async (e, invoice) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await invoicesAPI.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      notifyError('The PDF could not be downloaded.', err);
    }
  };

  // Section configuration with metadata and render functions
  const SECTION_CONFIGS = {
    invoices: {
      title: 'Open Invoices',
      icon: FileText,
      getCount: () => invoices.length,
      getSummary: () => invoices.length > 0 ? `${invoices.length} open invoice${invoices.length > 1 ? 's' : ''}` : 'No open invoices',
      viewAllLink: `/admin/invoices?company=${companyId}&status=open`,
    },
    payments: {
      title: 'Payment History',
      icon: CreditCard,
      getCount: () => payments.length,
      getSummary: () => payments.length > 0 ? `${payments.length} recent payment${payments.length > 1 ? 's' : ''}` : 'No payments',
      viewAllLink: `/admin/payments?companyId=${companyId}`,
    },
    contacts: {
      title: 'ConnectWise Contacts',
      icon: Users,
      getCount: () => contacts.length,
      getSummary: () => contacts.length > 0 ? `${contacts.length} contact${contacts.length > 1 ? 's' : ''}` : 'No contacts',
    },
    tickets: {
      title: 'Tickets Overview',
      icon: Ticket,
      getCount: () => ticketData.stats?.openCount || 0,
      getSummary: () => ticketData.stats ? `${ticketData.stats.openCount} open, ${ticketData.stats.closedThisMonth} closed this month` : 'No ticket data',
      viewAllLink: `/admin/tickets?company=${companyId}`,
    },
    paymentMethods: {
      title: 'Stored Payment Methods',
      icon: Wallet,
      getCount: () => paymentMethods.length,
      getSummary: () => paymentMethods.length > 0 ? `${paymentMethods.length} payment method${paymentMethods.length > 1 ? 's' : ''}` : 'No payment methods',
    },
    recurringPayments: {
      title: 'Recurring Payments',
      icon: RefreshCw,
      getCount: () => recurringPayments.filter(rp => rp.status === 'active').length,
      getSummary: () => {
        const active = recurringPayments.filter(rp => rp.status === 'active');
        return active.length > 0 ? `${active.length} active subscription${active.length > 1 ? 's' : ''}` : 'No recurring payments';
      },
      viewAllLink: `/admin/client-payments?company=${companyId}`,
    },
    autopay: {
      title: 'AutoPay Configuration',
      icon: RefreshCw,
      getCount: () => autopay?.is_enabled ? 1 : 0,
      getSummary: () => autopay?.is_enabled ? 'AutoPay Enabled' : 'AutoPay Disabled',
    },
    agreements: {
      title: 'Service Agreements',
      icon: FileSignature,
      getCount: () => agreements.length,
      getSummary: () => agreements.length > 0 ? `${agreements.length} agreement${agreements.length > 1 ? 's' : ''}` : 'No agreements',
    },
    projects: {
      title: 'Active Projects',
      icon: FolderKanban,
      getCount: () => projects.length,
      getSummary: () => projects.length > 0 ? `${projects.length} project${projects.length > 1 ? 's' : ''}` : 'No active projects',
    },
    configurations: {
      title: 'Configurations / Assets',
      icon: Monitor,
      getCount: () => configurations.length,
      getSummary: () => configurations.length > 0 ? `${configurations.length} configuration${configurations.length > 1 ? 's' : ''}` : 'No configurations',
    },
    opportunities: {
      title: 'Open Opportunities',
      icon: Target,
      getCount: () => opportunities.length,
      getSummary: () => opportunities.length > 0 ? `${opportunities.length} opportunit${opportunities.length > 1 ? 'ies' : 'y'}` : 'No open opportunities',
    },
    portalUsers: {
      title: 'Portal Users',
      icon: Users,
      getCount: () => users.length,
      getSummary: () => users.length > 0 ? `${users.length} registered user${users.length > 1 ? 's' : ''}` : 'No portal users',
    },
    featureFlags: {
      title: 'Feature Flags',
      icon: Settings,
      getCount: () => featureFlags.filter(f => f.hasOverride).length,
      getSummary: () => {
        const overrideCount = featureFlags.filter(f => f.hasOverride).length;
        return overrideCount > 0 ? `${overrideCount} override${overrideCount > 1 ? 's' : ''}` : 'Using system defaults';
      },
    },
    paymentSettings: {
      title: 'Payment Settings',
      icon: CreditCard,
      getCount: () => paymentSettings?.companySettings ? 1 : 0,
      getSummary: () => {
        if (!paymentSettings?.companySettings) return 'Using global defaults';
        const cs = paymentSettings.companySettings;
        if (cs.creditCardSurchargeEnabled !== null) {
          return cs.creditCardSurchargeEnabled
            ? `${cs.creditCardSurchargeType === 'fixed' ? '$' : ''}${cs.creditCardSurchargeValue}${cs.creditCardSurchargeType === 'percentage' ? '%' : ''} surcharge`
            : 'Surcharge disabled';
        }
        return 'Custom settings';
      },
    },
    displaySettings: {
      title: 'Display Settings',
      icon: Settings,
      getCount: () => Object.keys(displaySettings?.companyOverrides || {}).length,
      getSummary: () => {
        const count = Object.keys(displaySettings?.companyOverrides || {}).length;
        return count > 0
          ? `${count} custom ${count === 1 ? 'override' : 'overrides'}`
          : 'Using global defaults';
      },
    },
    branding: {
      title: 'Branding',
      icon: Palette,
      getCount: () => (branding?.effective?.overridden || []).length,
      getSummary: () => {
        const n = (branding?.effective?.overridden || []).length;
        return n > 0 ? `${n} custom ${n === 1 ? 'field' : 'fields'}` : 'Using MSP branding';
      },
    },
    comanaged: {
      title: 'Co-managed IT',
      icon: Wrench,
      getCount: () => (comanaged?.technicians || []).length,
      getSummary: () => {
        if (!comanaged?.entitled) return 'Not in your plan';
        if (!comanaged?.settings) return 'Not co-managed';
        if (!comanaged.settings.enabled) return 'Configured, switched off';
        const n = (comanaged.technicians || []).length;
        return `${n} technician${n === 1 ? '' : 's'}`;
      },
    },
    notes: {
      title: 'Internal Notes',
      icon: StickyNote,
      getCount: () => notes.length,
      getSummary: () => notes.length > 0 ? `${notes.length} note${notes.length > 1 ? 's' : ''}` : 'No notes',
    },
    tags: {
      title: 'Tags',
      icon: Tag,
      getCount: () => tags.length,
      getSummary: () => tags.length > 0 ? tags.map(t => t.name).join(', ') : 'No tags',
    },
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setTempSections([...companySections]);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setTempSections(null);
    setEditMode(false);
  };

  const handleSaveLayout = () => {
    setSectionOrder(tempSections);
    setTempSections(null);
    setEditMode(false);
  };

  const handleResetToDefaults = () => {
    setTempSections([...getDefaults()]);
  };

  const handleToggleSectionExpanded = (sectionId) => {
    setTempSections(prev =>
      prev.map(s => s.id === sectionId ? { ...s, defaultExpanded: !s.defaultExpanded } : s)
    );
  };

  const handleDrop = (draggedId, targetId) => {
    setTempSections(prev => {
      const newSections = [...prev];
      const draggedIndex = newSections.findIndex(s => s.id === draggedId);
      const targetIndex = newSections.findIndex(s => s.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const [draggedSection] = newSections.splice(draggedIndex, 1);
      newSections.splice(targetIndex, 0, draggedSection);

      return newSections;
    });
  };

  // Get current sections (temp during edit, saved otherwise)
  const currentSections = editMode ? tempSections : companySections;

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await adminCompanyAPI.addNote(companyId, newNote.trim());
      setNotes([res.data.note, ...notes]);
      setNewNote('');
    } catch (err) {
      notifyError('The note could not be saved.', err);
    } finally {
      setAddingNote(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      await adminCompanyAPI.updateNote(companyId, noteId, { note: editingNoteText.trim() });
      setNotes(notes.map(n => n.id === noteId ? { ...n, note: editingNoteText.trim() } : n));
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      notifyError('The note could not be saved.', err);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      await adminCompanyAPI.deleteNote(companyId, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      notifyError('The note could not be deleted.', err);
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await adminCompanyAPI.updateNote(companyId, note.id, { is_pinned: !note.is_pinned });
      setNotes(notes.map(n => n.id === note.id ? { ...n, is_pinned: !n.is_pinned } : n));
    } catch (err) {
      notifyError('The note could not be pinned.', err);
    }
  };

  // Tags handlers
  const handleAssignTag = async (tagId) => {
    try {
      await adminCompanyAPI.assignTag(companyId, tagId);
      const tag = allTags.find(t => t.id === tagId);
      if (tag) setTags([...tags, tag]);
    } catch (err) {
      notifyError('The tag could not be added.', err);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await adminCompanyAPI.removeTag(companyId, tagId);
      setTags(tags.filter(t => t.id !== tagId));
    } catch (err) {
      notifyError('The tag could not be removed.', err);
    }
  };

  // Feature flag toggle handler
  const handleToggleFeatureFlag = async (flag, newValue) => {
    const flagKey = flag.key;
    setFlagsLoading(prev => ({ ...prev, [flagKey]: true }));

    try {
      if (newValue === null) {
        // Remove override - use system default
        await adminFeatureFlagsAPI.removeCompanyOverride(companyId, flagKey);
      } else {
        // Set company override
        await adminFeatureFlagsAPI.setCompanyOverride(companyId, flagKey, newValue);
      }
      // Refresh flags
      const res = await adminFeatureFlagsAPI.getCompanyFlags(companyId);
      setFeatureFlags(res.data.flags || []);
    } catch (err) {
      notifyError('The feature setting could not be changed.', err);
    } finally {
      setFlagsLoading(prev => ({ ...prev, [flagKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleBack = () => {
    const returnUrl = sessionStorage.getItem('adminCompaniesReturnUrl');
    sessionStorage.removeItem('adminCompaniesReturnUrl');
    navigate(returnUrl || '/admin/companies', { state: { refresh: Date.now() } });
  };

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Companies
        </button>
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <button
        onClick={handleBack}
        className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Companies
      </button>

      {/* Company Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
            <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
                {company.company_name || `Company #${company.cw_company_id}`}
              </h1>
              {company.cw_status && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
                  {company.cw_status}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              <span>CW ID: {company.cw_company_id}</span>
              {company.cw_identifier && <span>Identifier: {company.cw_identifier}</span>}
              {company.cw_type && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{company.cw_type}</span>}
              {company.cw_territory && <span>{company.cw_territory}</span>}
            </div>
            {/* ConnectWise Contact Info */}
            {(company.cw_phone || company.cw_website || company.cw_address) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-neutral-600 dark:text-neutral-300">
                {company.cw_phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {company.cw_phone}
                  </span>
                )}
                {company.cw_website && (
                  <a
                    href={company.cw_website.startsWith('http') ? company.cw_website : `https://${company.cw_website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    {company.cw_website}
                  </a>
                )}
                {company.cw_address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {[company.cw_address.city, company.cw_address.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* User Stats - Only show if company has portal users */}
        {company.user_count > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
                <Users className="w-4 h-4" />
                Total Users
              </div>
              <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                {company.user_count}
              </p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
                <Shield className="w-4 h-4" />
                Company Admins
              </div>
              <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                {company.admin_count}
              </p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
                <UserCheck className="w-4 h-4" />
                Verified
              </div>
              <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                {company.verified_count}
              </p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
                <Clock className="w-4 h-4" />
                Last Active
              </div>
              <p className="text-sm font-medium text-neutral-900 dark:text-white mt-1">
                {formatDate(company.last_activity)}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">No Portal Users</span>
            </div>
            <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
              This company has no registered portal users. Invoice and payment data from ConnectWise is still available below.
            </p>
          </div>
        )}
      </div>

      {/* Invoice Stats Bar */}
      {invoiceStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
              <FileText className="w-4 h-4" />
              Open Invoices
            </div>
            <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
              {invoiceStats.openInvoices}
            </p>
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400 text-sm">
              <DollarSign className="w-4 h-4" />
              Total Owed
            </div>
            <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
              {formatCurrency(invoiceStats.totalOwed)}
            </p>
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 text-red-500 dark:text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4" />
              Overdue
            </div>
            <p className={`text-2xl font-semibold mt-1 ${invoiceStats.overdueAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-white'}`}>
              {formatCurrency(invoiceStats.overdueAmount)}
            </p>
            {invoiceStats.overdueCount > 0 && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                {invoiceStats.overdueCount} invoice{invoiceStats.overdueCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
              <CreditCard className="w-4 h-4" />
              Payments (30d)
            </div>
            <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">
              {formatCurrency(invoiceStats.paymentsLast30Days)}
            </p>
          </div>
        </div>
      )}

      {/* Layout Customization Bar */}
      <div className="flex items-center justify-between bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
          <Settings className="w-4 h-4" />
          <span>Section Layout</span>
        </div>
        {editMode ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleResetToDefaults}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white border border-neutral-200 dark:border-neutral-600 rounded-lg"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSaveLayout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
            >
              Save Layout
            </button>
          </div>
        ) : (
          <button
            onClick={handleEnterEditMode}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-200 dark:border-primary-600 hover:border-primary-300 rounded-lg"
          >
            <Settings className="w-4 h-4" />
            Customize Layout
          </button>
        )}
      </div>

      {/* Edit Mode Instructions */}
      {editMode && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium">Customize your layout</p>
          <p className="mt-1">Drag sections to reorder them. Toggle the checkbox to set which sections are expanded by default.</p>
        </div>
      )}

      {/* Dynamic Sections */}
      {currentSections?.map((section, index) => {
        const config = SECTION_CONFIGS[section.id];
        if (!config) return null;

        return (
          <DraggableSection
            key={section.id}
            section={{ ...section, index }}
            sectionConfig={config}
            editMode={editMode}
            onToggleExpanded={handleToggleSectionExpanded}
            onDrop={handleDrop}
          >
            {renderSectionContent(section.id, section.defaultExpanded, config)}
          </DraggableSection>
        );
      })}
    </div>
  );

  // Render the content for each section type
  function renderSectionContent(sectionId, defaultExpanded, config) {
    switch (sectionId) {
      case 'invoices':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            viewAllLink={config.viewAllLink}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {invoices.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No open invoices</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Invoice #</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Date</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Total</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Balance</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('adminInvoicesReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/admin/invoices/${invoice.id}`);
                      }}
                    >
                      <td className="py-2">
                        <span className="font-medium text-primary-600 hover:text-primary-700">
                          {invoice.invoiceNumber}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(invoice.date)}</td>
                      <td className="py-2 text-right text-neutral-900 dark:text-white">{formatCurrency(invoice.total)}</td>
                      <td className="py-2 text-right">
                        <span className={invoice.balance > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          {formatCurrency(invoice.balance)}
                        </span>
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={(e) => handleDownloadPdf(e, invoice)}
                          className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'payments':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            viewAllLink={config.viewAllLink}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {payments.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No payment history</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Date</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Method</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Amount</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(payment.created_at)}</td>
                      <td className="py-2 text-neutral-900 dark:text-white">
                        {payment.payment_type === 'card' && payment.card_last_four
                          ? `${payment.card_type || 'Card'} ****${payment.card_last_four}`
                          : payment.payment_type === 'bank'
                          ? payment.bank_name || 'Bank Account'
                          : payment.metadata?.paymentType || payment.payment_type || 'Payment'}
                      </td>
                      <td className="py-2 text-right font-medium text-neutral-900 dark:text-white">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          payment.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                            : payment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'contacts':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {contacts.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No contacts found in ConnectWise</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Name</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Email</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Phone</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Title</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Portal Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {contact.firstName} {contact.lastName}
                          </span>
                          {contact.defaultFlag && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 rounded">
                              Default
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{contact.email || '-'}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{contact.phone || '-'}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{contact.title || '-'}</td>
                      <td className="py-2 text-center">
                        {contact.portalUser ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Registered ({contact.portalUser.role.replace('_', ' ')})
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400 rounded-full">
                            <XCircle className="w-3 h-3" />
                            Not Registered
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'tickets':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
            viewAllLink={config.viewAllLink}
          >
            {ticketData.stats && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Open Tickets</p>
                  <p className="text-2xl font-semibold text-blue-700 dark:text-blue-300">{ticketData.stats.openCount}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">Closed This Month</p>
                  <p className="text-2xl font-semibold text-green-700 dark:text-green-300">{ticketData.stats.closedThisMonth}</p>
                </div>
                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-3">
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 font-medium">Total Tickets</p>
                  <p className="text-2xl font-semibold text-neutral-700 dark:text-neutral-200">{ticketData.stats.totalCount}</p>
                </div>
              </div>
            )}
            {ticketData.tickets.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No tickets found</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">ID</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Summary</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Priority</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ticketData.tickets.map((ticket) => (
                    <tr key={ticket.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('adminTicketsReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/tickets/${ticket.id}?from=admin`);
                      }}
                    >
                      <td className="py-2">
                        <span className="font-medium text-primary-600">#{ticket.id}</span>
                      </td>
                      <td className="py-2 text-neutral-900 dark:text-white truncate max-w-xs">{ticket.summary}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          ticket.status?.name?.toLowerCase().includes('closed')
                            ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                            : ticket.status?.name?.toLowerCase().includes('new')
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        }`}>
                          {ticket.status?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          ticket.priority?.id <= 2 ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                          ticket.priority?.id <= 4 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' :
                          'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                        }`}>
                          {ticket.priority?.name || '-'}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(ticket._info?.dateEntered || ticket.dateEntered)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'paymentMethods':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {paymentMethods.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No stored payment methods</p>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white dark:bg-neutral-600 rounded-lg border border-neutral-200 dark:border-neutral-600 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-neutral-500 dark:text-neutral-300" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            {method.payment_type === 'card'
                              ? `${method.card_type || 'Card'} ****${method.card_last_four || method.last_four}`
                              : `${method.bank_name || 'Bank'} ****${method.account_last_four || method.last_four}`}
                          </span>
                          {method.is_default && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300 rounded">
                              Default
                            </span>
                          )}
                        </div>
                        {method.payment_type === 'card' && method.expiry_date && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">Expires {method.expiry_date}</p>
                        )}
                        {method.createdByUser && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">Added by {method.createdByUser.first_name} {method.createdByUser.last_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        );

      case 'recurringPayments':
        const filteredRecurringPayments = showCancelledRecurring
          ? recurringPayments
          : recurringPayments.filter(rp => rp.status !== 'cancelled');
        const cancelledCount = recurringPayments.filter(rp => rp.status === 'cancelled').length;

        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
            viewAllLink={config.viewAllLink}
          >
            {recurringPayments.length === 0 ? (
              <div className="text-center py-6">
                <RefreshCw className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">No recurring payments configured</p>
                <Link
                  to={`/admin/client-payments?company=${companyId}`}
                  className="inline-flex items-center gap-1 mt-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 text-sm"
                >
                  Set up recurring payment <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Filter toggle for cancelled payments */}
                {cancelledCount > 0 && (
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => setShowCancelledRecurring(!showCancelledRecurring)}
                      className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                    >
                      <Filter className="w-3 h-3" />
                      {showCancelledRecurring ? 'Hide' : 'Show'} cancelled ({cancelledCount})
                    </button>
                  </div>
                )}

                {filteredRecurringPayments.map((rp) => (
                  <div key={rp.id} className={`p-3 rounded-lg ${rp.status === 'cancelled' ? 'bg-neutral-100 dark:bg-neutral-800/50' : 'bg-neutral-50 dark:bg-neutral-700/50'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${rp.status === 'cancelled' ? 'text-neutral-500 dark:text-neutral-400' : 'text-neutral-900 dark:text-white'}`}>{rp.name}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            rp.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                              : rp.status === 'cancelled'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                              : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-600 dark:text-neutral-300'
                          }`}>
                            {rp.status}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                          {formatCurrency(rp.amount)} / {rp.interval_length === 1 ? '' : rp.interval_length + ' '}{rp.interval_unit === 'months' ? 'month' : 'day'}{rp.interval_length > 1 ? 's' : ''}
                        </p>
                        {rp.cw_agreement_name && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            Linked to: {rp.cw_agreement_name}
                          </p>
                        )}
                        {/* Cancellation details */}
                        {rp.status === 'cancelled' && rp.cancelled_at && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            Cancelled {new Date(rp.cancelled_at).toLocaleDateString()}
                            {rp.cancelled_by_first_name && ` by ${rp.cancelled_by_first_name} ${rp.cancelled_by_last_name}`}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-neutral-500 dark:text-neutral-400">
                          {rp.payment_type === 'card'
                            ? `${rp.card_type || 'Card'} ****${rp.last_four}`
                            : `${rp.bank_name || 'Bank'} ****${rp.account_last_four || rp.last_four}`}
                        </p>
                        {rp.status === 'active' && rp.next_payment_date && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            Next: {new Date(rp.next_payment_date).toLocaleDateString()}
                          </p>
                        )}
                        {rp.payments_made > 0 && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {rp.payments_made} payment{rp.payments_made > 1 ? 's' : ''} ({formatCurrency(rp.total_paid)})
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Application mode and history toggle */}
                    <div className="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-600 flex items-center justify-between">
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        {rp.application_mode === 'auto'
                          ? (rp.cw_agreement_id ? 'Auto-apply to agreement invoices' : 'Auto-apply to oldest invoices')
                          : 'Manual (add to credits)'}
                      </span>
                      {(rp.transactions?.length > 0 || rp.payments_made > 0) && (
                        <button
                          onClick={() => setExpandedRecurringId(expandedRecurringId === rp.id ? null : rp.id)}
                          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                        >
                          <History className="w-3 h-3" />
                          {expandedRecurringId === rp.id ? 'Hide' : 'View'} History
                          {expandedRecurringId === rp.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      )}
                    </div>

                    {/* Transaction history */}
                    {expandedRecurringId === rp.id && rp.transactions && rp.transactions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-600">
                        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">Payment History</p>
                        <div className="space-y-1.5">
                          {rp.transactions.map((txn) => (
                            <div key={txn.id} className="flex items-center justify-between text-xs bg-white dark:bg-neutral-700 rounded px-2 py-1.5">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${txn.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                                <span className="text-neutral-600 dark:text-neutral-300">
                                  {new Date(txn.transaction_date).toLocaleDateString()}
                                </span>
                                {txn.occurrence_number && (
                                  <span className="text-neutral-400">#{txn.occurrence_number}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={txn.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                  {formatCurrency(txn.amount)}
                                </span>
                                <span className={`px-1 py-0.5 rounded text-[10px] ${
                                  txn.status === 'success'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                                }`}>
                                  {txn.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {rp.transactions.length >= 10 && (
                          <Link
                            to={`/admin/client-payments?company=${companyId}`}
                            className="inline-flex items-center gap-1 mt-2 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                          >
                            View all transactions <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    )}

                    {/* Show message if no transactions yet */}
                    {expandedRecurringId === rp.id && (!rp.transactions || rp.transactions.length === 0) && (
                      <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-600">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center py-2">
                          No payment transactions recorded yet
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        );

      case 'autopay':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {!autopay || !autopay.is_enabled ? (
              <div className="text-center py-6">
                <RefreshCw className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">AutoPay is not configured for this company</p>
              </div>
            ) : (
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium mb-3">
                  <CheckCircle className="w-5 h-5" />
                  AutoPay Enabled
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Payment Method</p>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {autopay.paymentMethod
                        ? autopay.paymentMethod.payment_type === 'card'
                          ? `${autopay.paymentMethod.card_type || 'Card'} ****${autopay.paymentMethod.card_last_four || autopay.paymentMethod.last_four}`
                          : `${autopay.paymentMethod.bank_name || 'Bank'} ****${autopay.paymentMethod.account_last_four || autopay.paymentMethod.last_four}`
                        : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Maximum Amount</p>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {autopay.max_amount ? formatCurrency(autopay.max_amount) : 'No limit'}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Day of Month</p>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {autopay.day_of_month || 'Any day'}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Configured By</p>
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {autopay.configuredByUser
                        ? `${autopay.configuredByUser.first_name} ${autopay.configuredByUser.last_name}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CollapsibleSection>
        );

      case 'agreements':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {agreements.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No service agreements found</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Name</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Type</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Start Date</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">End Date</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((agreement) => (
                    <tr key={agreement.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('adminAgreementsReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/admin/agreements/${agreement.id}`);
                      }}
                    >
                      <td className="py-2 font-medium text-primary-600">{agreement.name}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{agreement.type?.name || '-'}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(agreement.startDate)}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(agreement.endDate)}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          agreement.cancelledFlag ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                          agreement.expiredFlag ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300' :
                          'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        }`}>
                          {agreement.cancelledFlag ? 'Cancelled' : agreement.expiredFlag ? 'Expired' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'projects':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {projects.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No active projects found</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Name</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Manager</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Est. End</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Progress</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('projectsReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/projects/${project.id}`);
                      }}
                    >
                      <td className="py-2">
                        <span className="font-medium text-primary-600">
                          {project.name}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                          {project.status?.name || '-'}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{project.manager?.name || '-'}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(project.estimatedEnd)}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-neutral-200 dark:bg-neutral-600 rounded-full h-2">
                            <div
                              className="bg-primary-500 h-2 rounded-full"
                              style={{ width: `${project.percentComplete || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-neutral-600 dark:text-neutral-400">{project.percentComplete || 0}%</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="inline-flex items-center gap-1 text-xs text-primary-600">
                          View
                          <ExternalLink className="w-3 h-3" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'configurations':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {configurations.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No configurations found</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Name</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Type</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Serial Number</th>
                  </tr>
                </thead>
                <tbody>
                  {configurations.map((cfg) => (
                    <tr key={cfg.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('configurationsReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/assets/${cfg.id}`);
                      }}
                    >
                      <td className="py-2 font-medium text-primary-600">{cfg.name}</td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{cfg.type?.name || '-'}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          cfg.status?.name === 'Active' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                          cfg.status?.name === 'Inactive' ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}>
                          {cfg.status?.name || '-'}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600 font-mono text-xs">{cfg.serialNumber || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'opportunities':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {opportunities.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No open opportunities found</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Name</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Status</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Expected Close</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Value</th>
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Quote Status</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp) => (
                    <tr key={opp.id} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                      onClick={() => {
                        sessionStorage.setItem('adminQuotesReturnUrl', window.location.pathname + window.location.search);
                        navigate(`/quotes/${opp.id}?from=admin`);
                      }}
                    >
                      <td className="py-2 font-medium text-primary-600">{opp.name}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          opp.wonFlag ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                          opp.lostFlag ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                        }`}>
                          {opp.status?.name || '-'}
                        </span>
                      </td>
                      <td className="py-2 text-neutral-600 dark:text-neutral-300">{formatShortDate(opp.expectedCloseDate)}</td>
                      <td className="py-2 text-right text-neutral-600">
                        {opp.value ? formatCurrency(opp.value) : '-'}
                      </td>
                      <td className="py-2">
                        {opp.linkedQuote ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                            <CheckCircle className="w-3 h-3" />
                            {opp.linkedQuote.quoteStatus}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400 dark:text-neutral-500">No local quote</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <span className="text-xs text-primary-600">
                          View
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        );

      case 'featureFlags':
        // Group flags by category
        const flagsByCategory = featureFlags.reduce((acc, flag) => {
          const cat = flag.category || 'general';
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(flag);
          return acc;
        }, {});

        const categoryLabels = {
          modules: 'Modules',
          payments: 'Payments',
          tickets: 'Tickets',
          invoices: 'Invoices',
          quotes: 'Quotes',
          ui: 'User Interface',
          notifications: 'Notifications',
          general: 'General',
        };

        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {featureFlags.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No feature flags configured</p>
            ) : (
              <div className="space-y-6">
                {Object.entries(flagsByCategory).map(([category, flags]) => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3 uppercase tracking-wider">
                      {categoryLabels[category] || category}
                    </h4>
                    <div className="space-y-2">
                      {flags.map((flag) => {
                        const isLoading = flagsLoading[flag.key];
                        const locked = flag.entitled === false;
                        const effectiveValue = flag.effectiveValue === true || flag.effectiveValue === 'true';
                        const defaultValue = flag.defaultValue === true || flag.defaultValue === 'true';
                        // A locked flag reads as off and cannot be switched on for one
                        // company either; the backend answers 409 if it is tried.
                        const toggleDisabled = isLoading || (locked && !effectiveValue);

                        return (
                          <div
                            key={flag.key}
                            className={`grid grid-cols-[1fr_auto] gap-4 p-3 rounded-lg border ${
                              flag.hasOverride
                                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                                : 'bg-neutral-50 dark:bg-neutral-700/50 border-neutral-200 dark:border-neutral-600'
                            }`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-neutral-900 dark:text-white">{flag.name}</span>
                                {flag.hasOverride && (
                                  <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded">
                                    Override
                                  </span>
                                )}
                                {locked && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 rounded"
                                    title={notInPlan}
                                  >
                                    <Lock className="w-3 h-3" /> {notInPlan}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{flag.description}</p>
                              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 font-mono">{flag.key}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {flag.hasOverride && (
                                <button
                                  onClick={() => handleToggleFeatureFlag(flag, null)}
                                  disabled={isLoading}
                                  className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 underline"
                                >
                                  Reset
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  const newValue = !effectiveValue;
                                  // If toggling back to default, remove override
                                  if (newValue === defaultValue) {
                                    handleToggleFeatureFlag(flag, null);
                                  } else {
                                    handleToggleFeatureFlag(flag, newValue);
                                  }
                                }}
                                disabled={toggleDisabled}
                                title={locked ? notInPlan : undefined}
                                className={`relative w-11 h-6 rounded-full transition-colors ${
                                  toggleDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                } ${effectiveValue ? 'bg-green-500' : 'bg-neutral-300'}`}
                              >
                                <span
                                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                    effectiveValue ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        );

      case 'paymentSettings':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            <PaymentSettingsSection
              paymentSettings={paymentSettings}
              companyId={companyId}
              loading={paymentSettingsLoading}
              onUpdate={async (settings) => {
                setPaymentSettingsLoading(true);
                try {
                  await adminCompanyAPI.updatePaymentSettings(companyId, settings);
                  const res = await adminCompanyAPI.getPaymentSettings(companyId);
                  setPaymentSettings(res.data);
                } finally {
                  setPaymentSettingsLoading(false);
                }
              }}
            />
          </CollapsibleSection>
        );

      case 'branding':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            <CompanyBrandingSection
              branding={branding}
              loading={brandingLoading}
              onSave={async (overrides) => {
                setBrandingLoading(true);
                try {
                  await adminBrandingAPI.updateCompany(companyId, overrides);
                  const res = await adminBrandingAPI.getCompany(companyId);
                  setBranding(res.data);
                } finally {
                  setBrandingLoading(false);
                }
              }}
              onReset={async () => {
                setBrandingLoading(true);
                try {
                  await adminBrandingAPI.resetCompany(companyId);
                  const res = await adminBrandingAPI.getCompany(companyId);
                  setBranding(res.data);
                } finally {
                  setBrandingLoading(false);
                }
              }}
            />
          </CollapsibleSection>
        );

      case 'comanaged':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            <CompanyComanagedSection
              data={comanaged}
              loading={comanagedLoading}
              onSave={async (settings) => {
                setComanagedLoading(true);
                try {
                  await adminComanagedAPI.updateCompany(companyId, settings);
                  const res = await adminComanagedAPI.getCompany(companyId);
                  setComanaged(res.data);
                } finally {
                  setComanagedLoading(false);
                }
              }}
              onReset={async () => {
                setComanagedLoading(true);
                try {
                  await adminComanagedAPI.resetCompany(companyId);
                  const res = await adminComanagedAPI.getCompany(companyId);
                  setComanaged(res.data);
                } finally {
                  setComanagedLoading(false);
                }
              }}
            />
          </CollapsibleSection>
        );

      case 'displaySettings':
        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            <CompanyDisplaySettingsSection
              displaySettings={displaySettings}
              companyId={companyId}
              loading={displaySettingsLoading}
              onSave={async (nextOverrides) => {
                setDisplaySettingsLoading(true);
                try {
                  await displaySettingsAPI.adminUpdateCompany(companyId, { overrides: nextOverrides });
                  const res = await displaySettingsAPI.adminGetCompany(companyId);
                  setDisplaySettings(res.data);
                } finally {
                  setDisplaySettingsLoading(false);
                }
              }}
              onUseGlobalDefaults={async () => {
                setDisplaySettingsLoading(true);
                try {
                  await displaySettingsAPI.adminUpdateCompany(companyId, { useGlobalDefaults: true });
                  const res = await displaySettingsAPI.adminGetCompany(companyId);
                  setDisplaySettings(res.data);
                } finally {
                  setDisplaySettingsLoading(false);
                }
              }}
            />
          </CollapsibleSection>
        );

      case 'portalUsers':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-lg font-medium text-neutral-900 dark:text-white">Portal Users</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Users registered from this company
              </p>
            </div>

            {users.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <Users className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">No portal users registered</p>
                <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
                  Contacts from this company have not yet registered for the portal.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-primary-600 dark:text-primary-400 font-medium text-sm">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-neutral-900 dark:text-white">
                                  {user.first_name} {user.last_name}
                                </p>
                                {user.is_primary_contact && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 rounded">
                                    <Star className="w-3 h-3" />
                                    Primary
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
                                <Mail className="w-3 h-3" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${roleBadges[user.role] || roleBadges.user}`}>
                            {user.role.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {user.email_verified ? (
                            <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                              <UserCheck className="w-4 h-4" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-sm text-yellow-600 dark:text-yellow-400">
                              <Clock className="w-4 h-4" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                          {formatDate(user.last_login)}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                          {formatDate(user.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'notes':

        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {/* Add note form */}
            <div className="mb-4">
              <div className="flex gap-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add an internal note..."
                  className="flex-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white resize-none"
                  rows={2}
                />
                <button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed self-end"
                >
                  {addingNote ? '...' : 'Add'}
                </button>
              </div>
            </div>

            {/* Notes list */}
            {notes.length === 0 ? (
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No internal notes</p>
            ) : (
              <div className="space-y-3">
                {[...notes].sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)).map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg border ${
                      note.is_pinned
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                        : 'bg-neutral-50 dark:bg-neutral-700/50 border-neutral-200 dark:border-neutral-600'
                    }`}
                  >
                    {editingNoteId === note.id ? (
                      <div className="flex gap-2">
                        <textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 dark:text-white resize-none"
                          rows={2}
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleUpdateNote(note.id)}
                            className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                            className="p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-600 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-neutral-900 dark:text-white whitespace-pre-wrap">{note.note}</p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleTogglePin(note)}
                              className={`p-1 rounded ${note.is_pinned ? 'text-yellow-600' : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'}`}
                              title={note.is_pinned ? 'Unpin' : 'Pin'}
                            >
                              <Pin className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.note); }}
                              className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="p-1 text-neutral-400 hover:text-red-600 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                          {note.first_name} {note.last_name} · {formatShortDate(note.created_at)}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        );

      case 'tags':
        const tagColorMap = {
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 border-red-200 dark:border-red-700',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 border-green-200 dark:border-green-700',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 border-blue-200 dark:border-blue-700',
          purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 border-purple-200 dark:border-purple-700',
          cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300 border-orange-200 dark:border-orange-700',
          neutral: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-600',
        };
        const assignedTagIds = tags.map(t => t.id);
        const availableTags = allTags.filter(t => !assignedTagIds.includes(t.id));

        return (
          <CollapsibleSection
            title={config.title}
            count={config.getCount()}
            summary={config.getSummary()}
            icon={config.icon}
            defaultExpanded={defaultExpanded}
          >
            {/* Assigned tags */}
            <div className="mb-4">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase mb-2">Assigned Tags</p>
              {tags.length === 0 ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No tags assigned</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag.id}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full border ${tagColorMap[tag.color] || tagColorMap.neutral}`}
                    >
                      {tag.name}
                      <button
                        onClick={() => handleRemoveTag(tag.id)}
                        className="hover:opacity-70"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Available tags */}
            {availableTags.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase mb-2">Add Tag</p>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleAssignTag(tag.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium rounded-full border opacity-60 hover:opacity-100 transition-opacity ${tagColorMap[tag.color] || tagColorMap.neutral}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleSection>
        );

      default:
        return null;
    }
  }
}
